import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExtensionIcon from '@mui/icons-material/Extension';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  BUILDER_MECHANICS,
  BUILDER_RECIPES,
  BuilderPlugin,
  CatalogItem,
  COMMON_PLUGIN_NAMES,
  categoryLabel,
  pluginGroup,
  pluginInputs,
  pluginOutputFields,
  pluginToInsertMarkdown,
  searchCatalogItems,
  searchPlugins,
  sortPluginsForBuilder,
} from './chainBuilderCatalog';

export interface BuilderPaletteProps {
  plugins: BuilderPlugin[];
  loading?: boolean;
  loadError?: string | null;
  onInsert: (markdown: string) => void;
  disabled?: boolean;
  onHide?: () => void;
}

type PaletteTab = 'plugins' | 'recipes' | 'mechanics';

const GROUP_LABELS: Record<string, string> = {
  common: 'Common building blocks',
  general: 'Also useful',
  specialized: 'Specialized (meal plans, ingest, …)',
};

export const BuilderPalette: React.FC<BuilderPaletteProps> = ({
  plugins,
  loading = false,
  loadError = null,
  onInsert,
  disabled,
  onHide,
}) => {
  const [tab, setTab] = useState<PaletteTab>('plugins');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [justInserted, setJustInserted] = useState<string | null>(null);

  const categories = useMemo(() => {
    const counts: Record<string, number> = {};
    plugins.forEach((p) => {
      const key = p.category || 'other';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => categoryLabel(a[0]).localeCompare(categoryLabel(b[0])));
  }, [plugins]);

  const filteredPlugins = useMemo(() => {
    let list = searchPlugins(plugins, query);
    if (category !== 'all') list = list.filter((p) => (p.category || 'other') === category);
    return sortPluginsForBuilder(list);
  }, [plugins, query, category]);

  const groupedPlugins = useMemo(() => {
    if (query.trim() || category !== 'all') return null;
    const groups: Array<{ key: 'common' | 'general' | 'specialized'; items: BuilderPlugin[] }> = [
      { key: 'common', items: [] },
      { key: 'general', items: [] },
      { key: 'specialized', items: [] },
    ];
    filteredPlugins.forEach((p) => {
      const g = pluginGroup(p);
      const bucket = groups.find((x) => x.key === g);
      if (bucket) bucket.items.push(p);
    });
    return groups.filter((g) => g.items.length > 0);
  }, [filteredPlugins, query, category]);

  const recipes = useMemo(() => searchCatalogItems(BUILDER_RECIPES, query), [query]);
  const mechanics = useMemo(() => searchCatalogItems(BUILDER_MECHANICS, query), [query]);

  const insert = (id: string, markdown: string) => {
    onInsert(markdown);
    setJustInserted(id);
    window.setTimeout(() => setJustInserted((cur) => (cur === id ? null : cur)), 1200);
  };

  const placeholder =
    tab === 'plugins' ? 'Search plugins, outputs (sample, columns…)' :
    tab === 'recipes' ? 'Search recipes (csv, batch, upload…)' :
    'Search how-to (inputs, looping, constraints…)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Insert into prompt
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Click Insert to drop a snippet into the prompt.
          </Typography>
        </Box>
        {onHide && (
          <Tooltip title="Hide this panel (your choice is remembered)">
            <Button
              size="small"
              onClick={onHide}
              endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
              sx={{ textTransform: 'none', flexShrink: 0, minWidth: 0, px: 1 }}
            >
              Hide
            </Button>
          </Tooltip>
        )}
      </Box>

      <TextField
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{ minHeight: 36, mb: 1, '& .MuiTab-root': { minHeight: 36, py: 0, fontSize: '0.75rem', textTransform: 'none' } }}
      >
        <Tab value="plugins" icon={<ExtensionIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Plugins (${plugins.length})`} />
        <Tab value="recipes" icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Recipes" />
        <Tab value="mechanics" icon={<MenuBookIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="How-to" />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
        {tab === 'plugins' && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip
                size="small"
                label={`All (${plugins.length})`}
                color={category === 'all' ? 'primary' : 'default'}
                variant={category === 'all' ? 'filled' : 'outlined'}
                onClick={() => setCategory('all')}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
              {categories.map(([key, n]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${categoryLabel(key)} (${n})`}
                  color={category === key ? 'primary' : 'default'}
                  variant={category === key ? 'filled' : 'outlined'}
                  onClick={() => setCategory(key)}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              ))}
            </Box>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {loadError && <Alert severity="warning" sx={{ mb: 1 }}>{loadError}</Alert>}
            {!loading && filteredPlugins.length === 0 && (
              <Typography variant="caption" color="text.secondary">No plugins match “{query}”.</Typography>
            )}
            {groupedPlugins
              ? groupedPlugins.map((group) => (
                <Box key={group.key} sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3, display: 'block', mb: 0.5, mt: 0.5 }}>
                    {GROUP_LABELS[group.key]}
                  </Typography>
                  {group.items.map((plugin) => (
                    <PluginCard
                      key={plugin.name}
                      plugin={plugin}
                      inserted={justInserted === plugin.name}
                      disabled={disabled}
                      onInsert={() => insert(plugin.name, pluginToInsertMarkdown(plugin))}
                    />
                  ))}
                </Box>
              ))
              : filteredPlugins.map((plugin) => (
                <PluginCard
                  key={plugin.name}
                  plugin={plugin}
                  inserted={justInserted === plugin.name}
                  disabled={disabled}
                  onInsert={() => insert(plugin.name, pluginToInsertMarkdown(plugin))}
                />
              ))}
          </>
        )}

        {tab === 'recipes' && (
          <>
            {recipes.length === 0 && (
              <Typography variant="caption" color="text.secondary">No recipes match “{query}”.</Typography>
            )}
            {recipes.map((item) => (
              <SnippetCard
                key={item.id}
                item={item}
                inserted={justInserted === item.id}
                disabled={disabled}
                onInsert={() => insert(item.id, item.insertMarkdown)}
              />
            ))}
          </>
        )}

        {tab === 'mechanics' && (
          <>
            {mechanics.length === 0 && (
              <Typography variant="caption" color="text.secondary">No how-to items match “{query}”.</Typography>
            )}
            {mechanics.map((item) => (
              <SnippetCard
                key={item.id}
                item={item}
                inserted={justInserted === item.id}
                disabled={disabled}
                onInsert={() => insert(item.id, item.insertMarkdown)}
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

const PluginCard: React.FC<{
  plugin: BuilderPlugin;
  inserted: boolean;
  disabled?: boolean;
  onInsert: () => void;
}> = ({ plugin, inserted, disabled, onInsert }) => {
  const outputs = pluginOutputFields(plugin);
  const inputs = pluginInputs(plugin);
  const title = plugin.display_name || plugin.name;
  return (
    <Accordion disableGutters elevation={0} sx={{ mb: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={{ minHeight: 40, px: 1, '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 0.75, overflow: 'hidden' } }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }} noWrap>
              {title}
            </Typography>
            {COMMON_PLUGIN_NAMES.includes(plugin.name) && (
              <Chip size="small" label="Common" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            plugin:{plugin.name}
          </Typography>
        </Box>
        <Tooltip title="Insert this plugin into the prompt">
          <span>
            <Button
              size="small"
              variant={inserted ? 'contained' : 'outlined'}
              color={inserted ? 'success' : 'primary'}
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              disabled={disabled}
              onClick={(e) => { e.stopPropagation(); onInsert(); }}
              sx={{ textTransform: 'none', flexShrink: 0, minWidth: 0, px: 1 }}
            >
              {inserted ? 'Added' : 'Insert'}
            </Button>
          </span>
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 1.25, pb: 1.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, whiteSpace: 'pre-wrap' }}>
          {plugin.description || plugin.usage_info?.summary || 'Code plugin.'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
          <Chip size="small" label={categoryLabel(plugin.category)} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          {inputs.length > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.25 }}>In:</Typography>
          )}
          {inputs.slice(0, 8).map((inp) => (
            <Chip
              key={inp.name}
              size="small"
              label={`${inp.name}${inp.required ? '' : '?'}`}
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          ))}
        </Box>
        {outputs.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.25 }}>Out:</Typography>
            {outputs.map((f) => (
              <Chip key={f} size="small" color="primary" variant="outlined" label={f} sx={{ height: 20, fontSize: '0.65rem' }} />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const SnippetCard: React.FC<{
  item: CatalogItem;
  inserted: boolean;
  disabled?: boolean;
  onInsert: () => void;
}> = ({ item, inserted, disabled, onInsert }) => (
  <Box sx={{ mb: 0.75, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{item.title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.hint}</Typography>
      </Box>
      <Button
        size="small"
        variant={inserted ? 'contained' : 'outlined'}
        color={inserted ? 'success' : 'primary'}
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        disabled={disabled}
        onClick={onInsert}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {inserted ? 'Added' : 'Insert'}
      </Button>
    </Box>
  </Box>
);

/** Items for @-mention autocomplete (plugins + recipes + how-to). */
export function builderMentionItems(plugins: BuilderPlugin[]): Array<{ id: string; label: string; markdown: string }> {
  return [
    ...sortPluginsForBuilder(plugins).map((p) => ({
      id: `plugin:${p.name}`,
      label: `${p.display_name || p.name}  (plugin:${p.name})`,
      markdown: pluginToInsertMarkdown(p),
    })),
    ...BUILDER_RECIPES.map((r) => ({ id: r.id, label: `Recipe: ${r.title}`, markdown: r.insertMarkdown })),
    ...BUILDER_MECHANICS.map((m) => ({ id: m.id, label: `How-to: ${m.title}`, markdown: m.insertMarkdown })),
  ];
}
